/**
 * Small shared pieces for the Business Development screens.
 */
import React from 'react';
import { X } from 'lucide-react';
import { Stage, STAGE_LABEL } from './bdApi';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from '../../../components/shadcn/ui/sheet';
import { useReturnFocus } from '../../../components/shadcn/ui/dialog';
import FilledStatusChip from '../../../components/status/FilledStatusChip';
import { SNF_FACILITIES_STATUS, MONDAY_BLANK } from '../../../theme/mondayPalette';

/**
 * A right-hand drawer on the shadcn Sheet (Radix Dialog): a true modal. Focus is trapped and moves in on open, Escape
 * and the scrim close it, focus goes back to what opened it, the page behind is inert and does not scroll. The title
 * names it; the description, when given, describes it.
 */
export function Drawer({ open, onClose, title, description, scope, children }: {
  open: boolean; onClose: () => void; title: React.ReactNode; description?: React.ReactNode;
  /** The shell class whose tokens the body needs (e.g. `um`): the drawer is portalled under body, outside the shell. */
  scope?: string;
  children: React.ReactNode;
}) {
  const returnFocus = useReturnFocus(open);
  // Unmounted the moment `open` is false, not left to Radix's exit pass: a caller that moves focus right after closing
  // (a section heading after an act) must not be pulled back into a still-trapping drawer.
  if (!open) return null;
  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        hideClose
        onCloseAutoFocus={returnFocus}
        {...(description ? {} : { 'aria-describedby': undefined })}
        className="h-full w-full gap-0 overflow-y-auto border-l-0 p-0 shadow-xl sm:max-w-2xl"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b bg-background px-4 py-3">
          <div className="min-w-0">
            <SheetTitle className="break-words">{title}</SheetTitle>
            {description ? <SheetDescription>{description}</SheetDescription> : null}
          </div>
          <SheetClose aria-label="Close" className="rounded p-1 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <X className="w-4 h-4" />
          </SheetClose>
        </div>
        {scope ? <div className={scope} style={{ minHeight: 0, background: 'transparent' }}>{children}</div> : children}
      </SheetContent>
    </Sheet>
  );
}

/**
 * The TM board's Status words (SNF Facilities) carry Monday's colours; a stage
 * the board has no word for (Prospect, BAA sent, BAA signed) takes Monday's
 * neutral grey rather than an invented hue, and the word says which it is.
 */
const STAGE_BOARD_WORD: Partial<Record<Stage, string>> = {
  COLD: 'COLD', INTERESTED: 'INTERESTED', ACTIVE: 'ACTIVE', DROPPED: 'DROPPED', DO_NOT_CONTACT: 'DONT BUG',
};

export function StagePill({ stage, board }: { stage: Stage; board?: Stage | null }) {
  const disagrees = board && board !== stage;
  return (
    <span className="inline-flex items-center gap-1">
      <FilledStatusChip
        word={STAGE_BOARD_WORD[stage] || stage}
        hex={STAGE_BOARD_WORD[stage] ? undefined : MONDAY_BLANK.hex}
        column={SNF_FACILITIES_STATUS}
        label={STAGE_LABEL[stage]}
        size="sm"
      />
      {disagrees && (
        <span className="text-[11px] text-muted-foreground" title="What the TM board says">
          board: {STAGE_LABEL[board as Stage]}
        </span>
      )}
    </span>
  );
}

export function NativeSelect({
  label, value, onChange, children, className = '', hideLabel = false,
}: {
  label: string; value: string; onChange: (v: string) => void; children: React.ReactNode; className?: string; hideLabel?: boolean;
}) {
  const id = React.useId();
  return (
    <label htmlFor={id} className={`flex flex-col gap-1 text-xs text-muted-foreground ${className}`}>
      <span className={hideLabel ? 'sr-only' : ''}>{label}</span>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {children}
      </select>
    </label>
  );
}

export function Stat({ label, value, sub }: { label: string; value: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold leading-tight" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

/** Loading, failure and empty are three different sentences. */
export function StateLine({ loading, error, empty, onRetry }: { loading?: boolean; error?: string | null; empty?: string | null; onRetry?: () => void }) {
  if (loading) return <p role="status" className="py-6 text-sm text-muted-foreground">Loading…</p>;
  if (error) {
    return (
      <div role="alert" className="flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm">
        <span>{error}</span>
        {onRetry && <button type="button" onClick={onRetry} className="underline underline-offset-2">Try again</button>}
      </div>
    );
  }
  if (empty) return <p className="py-6 text-sm text-muted-foreground">{empty}</p>;
  return null;
}
